import React, { Component } from 'react';
import MultiSelect from 'react-multi-select-component';
import moment from 'moment';
import { parseDoubtTime, formatDoubtTime, getSubjectIcon } from './doubtHelpers';

class DoubtList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: ''
        };
    }

    hasActiveFilters = () => {
        const statusFilter = this.props.statusFilter || 'all';
        const selectedSubjects = this.props.selectedSubjects || [];
        const subjects = this.props.subjects || [];
        // every subject ticked is the neutral state, same as none
        const subjectsNarrowed = selectedSubjects.length > 0 && selectedSubjects.length < subjects.length;

        return !!(this.state.searchTerm || statusFilter !== 'all' || this.props.dateNarrowed || subjectsNarrowed);
    }

    clearFilters = () => {
        this.setState({ searchTerm: '' });
        this.props.onStatusFilterChange('all');
        if (this.props.onResetDates) this.props.onResetDates();
        if (this.props.onSubjectsChange) this.props.onSubjectsChange(this.props.subjects || []);
    }

    render() {
        const doubts = this.props.doubts || [];
        const statusFilter = this.props.statusFilter || 'all';
        const statusOptions = this.props.statusOptions || [];
        const selectedSubjects = this.props.selectedSubjects || [];
        const subjectOptions = (this.props.subjects || []).map(s => ({ value: s, label: s }));
        const rangeStart = this.props.rangeStart;
        const rangeEnd = this.props.rangeEnd;

        // Search + date first, so the status counts below stay stable
        // whichever status is currently picked.
        const baseDoubts = doubts.filter(doubt => {
            const searchLower = this.state.searchTerm.toLowerCase();
            const matchSearch = (doubt.title || '').toLowerCase().includes(searchLower) ||
                                (doubt.description || '').toLowerCase().includes(searchLower);

            let matchDate = true;
            const doubtDate = parseDoubtTime(doubt.raised_time || doubt.timestamp);
            if (doubtDate && rangeStart && rangeEnd) {
                matchDate = doubtDate.isBetween(
                    moment(rangeStart).startOf('day'),
                    moment(rangeEnd).endOf('day'),
                    null,
                    '[]'
                );
            }

            return matchSearch && matchDate;
        });

        const countForStatus = (id) =>
            baseDoubts.filter(d => d.doubt_status.toString() === id.toString()).length;

        const filteredDoubts = statusFilter === 'all'
            ? baseDoubts
            : baseDoubts.filter(d => d.doubt_status.toString() === statusFilter.toString());

        // Resolved threads settle to the bottom of the register
        const orderedDoubts = filteredDoubts
            .map((doubt, index) => ({ doubt, index }))
            .sort((a, b) => ((a.doubt.doubt_status == 3) - (b.doubt.doubt_status == 3)) || (a.index - b.index))
            .map(entry => entry.doubt);

        const selectedId = this.props.selectedDoubt ? this.props.selectedDoubt.id : null;

        return (
            <React.Fragment>
                <div className="dd-threads-head">
                    <input
                        type="text"
                        className="dd-field dd-search"
                        placeholder="Search the register..."
                        value={this.state.searchTerm}
                        onChange={(e) => this.setState({ searchTerm: e.target.value })}
                    />

                    <div className="dd-filter-row">
                        <div className="dd-subject-select">
                            <MultiSelect
                                options={subjectOptions}
                                value={selectedSubjects.map(s => ({ value: s, label: s }))}
                                onChange={(picked) => this.props.onSubjectsChange((picked || []).map(p => p.value))}
                                labelledBy="Subjects"
                                hasSelectAll={true}
                                disableSearch={subjectOptions.length < 8}
                                overrideStrings={{
                                    selectAll: 'All Subjects',
                                    allItemsAreSelected: 'All Subjects',
                                    selectSomeItems: 'All Subjects',
                                    search: 'Search subjects',
                                    noOptions: 'No subjects yet'
                                }}
                                valueRenderer={(selected, options) => {
                                    if (!selected.length || selected.length === options.length) return 'All Subjects';
                                    if (selected.length === 1) return selected[0].label;
                                    return `${selected.length} subjects`;
                                }}
                            />
                        </div>
                        <select
                            className="dd-field"
                            value={statusFilter}
                            onChange={(e) => this.props.onStatusFilterChange(e.target.value)}
                        >
                            <option value="all">All status ({baseDoubts.length})</option>
                            {statusOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.name} ({countForStatus(opt.id)})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {orderedDoubts.length === 0 ? (
                    <div className="dd-empty">
                        <div className="dd-glyph">&#167;</div>
                        {this.hasActiveFilters()
                            ? 'No doubts match the current filters.'
                            : 'No doubts logged here yet — ask one below.'}
                        {this.hasActiveFilters() ? (
                            <button className="dd-empty-btn" onClick={this.clearFilters}>
                                Clear filters
                            </button>
                        ) : (
                            <button className="dd-empty-btn" onClick={this.props.onSwitchToCreate}>
                                Ask a new doubt
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="dd-thread-list">
                        {orderedDoubts.map((doubt) => {
                            const isSelected = selectedId !== null && selectedId.toString() === doubt.id.toString();
                            const hasUnseen = (doubt.unseen_count || 0) > 0;

                            return (
                                <div
                                    key={doubt.id}
                                    className={`dd-thread-item ${isSelected ? 'active' : ''}`}
                                    onClick={() => this.props.onSelectDoubt(doubt)}
                                >
                                    <div className="dd-thread-top">
                                        <span className="dd-thread-subject">
                                            <i className={getSubjectIcon(doubt.subject_name)} />
                                            {doubt.subject_name || 'General'}
                                        </span>
                                        <span className={this.props.getStampClass(doubt.doubt_status)}>
                                            {this.props.getStatusName(doubt.doubt_status)}
                                        </span>
                                    </div>
                                    <div className="dd-thread-who">
                                        <span>{doubt.title}</span>
                                        {hasUnseen && <span className="dd-unseen">{doubt.unseen_count}</span>}
                                    </div>
                                    <div className="dd-thread-snip">{doubt.description}</div>
                                    <div className="dd-thread-bottom">
                                        <span className="dd-thread-time">{formatDoubtTime(doubt.raised_time || doubt.timestamp)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </React.Fragment>
        );
    }
}

export default DoubtList;
